import { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PopupHost } from '@safetyhub/ui';
import { useAuthStore } from '../store/auth';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { adminApi } from '../lib/api';
import { getPendingVerifications } from '../lib/verification';
import { syncLogsApi } from '../lib/sync-logs';
import { ADMIN_ANNOUNCEMENT_QUERY_KEYS } from '../lib/announcements';
import { LEGAL_OBLIGATION_CATALOG_QUERY_KEYS } from '../lib/legalObligationCatalog';
import { LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS } from '../lib/legalObligationOverlays';
import { CHEMICAL_LINKS_QUERY_KEYS } from '../lib/chemicalLinks';
import { dismissPopup, fetchActivePopups, markPopupSeen } from '../lib/popups';
import { OPS_INCIDENT_QUERY_KEYS, opsIncidentsApi } from '../lib/opsIncidents';
import type { AdminPermissionKey } from '../lib/adminPermissionKeys';

// 사이드바 네비게이션 아이템 타입
type IconComp = React.FC<{ className?: string }>;

interface NavLeaf {
  type?: 'item';
  path: string;
  label: string;
  icon: IconComp | null;
  flagKey?: string; // 피처 플래그 키 - 설정 시 해당 플래그가 활성화된 경우에만 표시
  // 권한 키 - 설정 시 해당 권한이 있는 경우에만 표시. 정본 키 타입이라 오타는 tsc red다(사본 동기화: `lib/adminPermissionKeys.ts`)
  permissionKey?: AdminPermissionKey;
}

interface NavGroup {
  type: 'group';
  id: string;
  label: string;
  icon: string | IconComp; // emoji 또는 leaf와 같은 SVG 컴포넌트
  items: NavLeaf[];
}

interface NavDivider {
  type: 'divider';
}

type NavItem = NavLeaf | NavGroup | NavDivider;

// 사이드바 네비게이션 (주요 관리 기능)
const sideNavItems: NavItem[] = [
  { path: '/', label: '대시보드', icon: DashboardIcon },
  { path: '/organizations', label: '조직 관리', icon: OrganizationIcon },
  { path: '/companies', label: '사업장 관리', icon: CompaniesIcon },
  { path: '/sites', label: '현장 관리', icon: SitesIcon },
  { path: '/users', label: '사용자 관리', icon: UsersIcon },
  { path: '/inspections', label: '점검 관리', icon: InspectionsIcon },
  { path: '/reports', label: '보고서 관리', icon: ReportsIcon, permissionKey: 'monitoring-view' },
  { path: '/ai-matching', label: 'AI 분석 이력', icon: AiMatchingIcon },
  // 머신러닝 소스 — 메뉴는 **하나**다. 소스(점검 사진·모범사례·개선 이미지…)는 그 페이지의
  // 탭으로 가르고, 소스가 늘면 탭이 늘어난다(2026-10-07 사용자 결정). 메뉴를 늘리지 말 것.
  // 서버 `AdminMlController`의 클래스 레벨 `monitoring-view`와 같은 축 — 종전엔 키가 없어 권한 없는
  // 관리자에게도 보였고 눌러서 403만 받았다(리뷰 1103 F6)
  { path: '/ml-sources', label: '머신러닝 소스 관리', icon: MlSourcesIcon, permissionKey: 'monitoring-view' },
  {
    type: 'group',
    id: 'monitoring',
    label: '모니터링',
    icon: '📊',
    items: [
      { path: '/operations', label: '종합 현황', icon: OperationsIcon, permissionKey: 'monitoring-view' },
      // 운영 사건 — 서버 `OpsIncidentsController`의 클래스 레벨 `monitoring-view`와 같은 축이다
      { path: '/ops-incidents', label: '운영 사건', icon: OperationsIcon, permissionKey: 'monitoring-view' },
      { path: '/sync-history', label: '데이터 동기화 이력', icon: SyncHistoryIcon, permissionKey: 'monitoring-view' },
      { path: '/ai-usage', label: 'AI 사용량', icon: AIUsageIcon, permissionKey: 'monitoring-view' },
      // 유저 스토리 모니터 — 읽기는 `monitoring-view`, 쓰기(확인 처리·수동 판정 기록)는
      // 서버가 `system-settings`로 따로 막는다. 메뉴는 읽기 권한을 따른다.
      { path: '/story-monitor', label: '유저 스토리 모니터', icon: StoryMonitorIcon, permissionKey: 'monitoring-view' },
    ],
  },
  {
    type: 'group',
    id: 'ops-management',
    label: '운영 관리',
    icon: '⚙️',
    items: [
      { path: '/tasks', label: '예약 작업', icon: TasksIcon },
      { path: '/external-services', label: '외부 서비스', icon: ExternalServicesIcon },
      { path: '/usage', label: '사용량 관리', icon: AIUsageIcon, permissionKey: 'system-settings' },
      { path: '/feature-flags', label: '피처 플래그', icon: FeatureFlagsIcon },
      // 조직 시스템 역할·권한 정의(company-rbac P3) — `/roles`(관리자 역할)와 다른 축
      { path: '/company-roles', label: '조직 역할·권한', icon: CompanyRolesIcon, permissionKey: 'system-settings' },
      // 법정 의무 카탈로그 — `permissionKey`가 서버 `AdminLegalObligationsController`의 클래스 레벨
      // `@RequirePermission('legal-obligation-catalog')`와 **같은 축**이다(읽기·쓰기 공용 키). ⛔ 피처
      // 플래그(`legal-obligation`)를 `flagKey`로 걸지 말 것 — 카탈로그를 채우는 일이 플래그를 켜기 «전»에
      // 해야 하는 일이다(그 컨트롤러 docblock의 「서로를 기다리는 교착」).
      { path: '/legal-obligation-catalog', label: '법정 의무 카탈로그', icon: RegulationsIcon, permissionKey: 'legal-obligation-catalog' },
      // 기관 서식 변형 — 서버 `AdminLegalObligationOverlaysController`가 카탈로그와 **같은 키**를 쓴다(그 docblock).
      // 플래그를 걸지 않는 이유도 위 카탈로그 줄과 같다(채우는 일이 켜기 «전»의 준비다)
      { path: '/legal-obligation-overlays', label: '기관 서식 변형', icon: RegulationsIcon, permissionKey: 'legal-obligation-catalog' },
      // 화학물질 연결(국소배기 49종 ↔ 물질 ↔ 별표 21) — 서버 `AdminChemicalLinksController`가 카탈로그와 **같은 키**를 쓴다(그 docblock)
      { path: '/chemical-links', label: '화학물질 연결', icon: RegulationsIcon, permissionKey: 'legal-obligation-catalog' },
    ],
  },
  { path: '/report-composer', label: '보고서 양식 빌더', icon: ReportComposerIcon, permissionKey: 'report-builder' },
  { type: 'divider' }, // 구분선
  { path: '/verification', label: '기관 인증', icon: VerificationIcon },
  { path: '/message-send', label: '메시지 발송', icon: MessageSendIcon },
  { path: '/feedback', label: 'AI 피드백', icon: FeedbackIcon },
  { path: '/user-feedback', label: '사용자 피드백', icon: UserFeedbackIcon },
  {
    type: 'group',
    id: 'classification',
    label: '분류 체계 관리',
    icon: TaxonomyIcon,
    items: [
      { path: '/industries', label: '업종 관리', icon: IndustriesIcon, permissionKey: 'system-settings' },
      { path: '/work-classifications', label: '공종-작업 분류 관리', icon: WorkClassificationsIcon },
      { path: '/industry-work-categories', label: '업종별 공종 분류 관리', icon: IndustryWorkCategoriesIcon },
      { path: '/hazard-categories', label: '위험 카테고리 관리', icon: HazardCategoriesIcon },
      { path: '/hazard-types', label: '위험유형 관리', icon: HazardTypesIcon, permissionKey: 'system-settings' },
    ],
  },
  { path: '/measures', label: '위험 대응 지식베이스', icon: MeasuresIcon },
  { path: '/regulations', label: '법규 관리', icon: RegulationsIcon },
  { path: '/accident-cases', label: '사고사례 관리', icon: AccidentCasesIcon },
  // 라벨은 「업데이트 내역」 — 우리가 쓰는 말로 통일했다(사용자 결정 2026-10-16).
  //
  // ## 노출면이 둘이다 (2026-10-21, 결정 채택 ④·⑥)
  // **소비**(이 행)는 수퍼관리자 **전원**이 읽는 자리라 `permissionKey`를 **달지 않는다** —
  // 서버 `AdminReleaseNoteFeedController`도 `@RequirePermission`을 두지 않는다(같은 축).
  // **관리**(아래 행)는 숨김·복구·노출 대상이라 `system-settings` 뒤다.
  // ⚠️ 소비를 **먼저** 둔다 — 대다수 운영자에게는 그쪽만 보이고, 「관리」가 그 변종임이 순서로
  //    읽힌다. 두 행을 떨어뜨려 놓지 말 것(같은 개념의 두 면이다).
  { path: '/release-note-feed', label: '업데이트 내역', icon: ReleaseNotesIcon },
  // ⚠️ `permissionKey`가 서버 컨트롤러의 게이트와 **같은 축**이어야 한다 —
  //    `AdminReleaseNotesController`가 클래스 레벨 `@RequirePermission('system-settings')`이므로
  //    그 권한이 없는 역할에게 메뉴를 보이면 눌러서 **403만 받는다**(바로 아래 `/announcements`가
  //    같은 규율의 선례다). ⚠️ 2026-10-21 전에는 이 행에 `permissionKey`가 **없었고** 그것이
  //    서버와 일치하는 상태였다 — 서버에 게이트가 생겨 **전제가 바뀌었다**. `audience-options.ts`가
  //    그때의 서술을 들고 있으니 함께 읽을 것(그 파일의 「권한 축」 절).
  { path: '/release-notes', label: '업데이트 내역 관리', icon: ReleaseNotesIcon, permissionKey: 'system-settings' },
  // 전사 공지(줄기 popup-and-announcement-surface P3-u) — 업데이트 내역 **옆**에 둔다. 둘 다
  // 「우리가 고객에게 말하는 자리」이고, 공지는 게시판 + 선택 병행 팝업이다.
  // ⚠️ `permissionKey`가 서버 컨트롤러의 게이트와 **같은 축**이어야 한다 —
  //    `AdminAnnouncementsController`가 `@RequirePermission('system-settings')`이므로 그 권한이
  //    없는 역할에게 메뉴를 보이면 눌러서 403만 받는다. 조직 RBAC의 `announcement.manage`는
  //    **다른 축**이니 여기 쓰지 말 것(그 키를 쓰면 조직 권한으로 전사 발행이 열린다).
  { path: '/announcements', label: '전사 공지', icon: AnnouncementsIcon, permissionKey: 'system-settings' },
  // 매뉴얼은 전원 열람 — permissionKey를 붙이지 않는다.
  { path: '/manual', label: '매뉴얼', icon: ManualIcon },
  { path: '/user-groups', label: '사용자 그룹', icon: UserGroupsIcon },
];

// 그룹 path 멤버십 매핑 (라우트 → 그룹 id)
const GROUP_PATH_MAP: Record<string, string> = {
  '/operations': 'monitoring',
  '/ops-incidents': 'monitoring',
  '/sync-history': 'monitoring',
  '/ai-usage': 'monitoring',
  '/story-monitor': 'monitoring',
  '/tasks': 'ops-management',
  '/external-services': 'ops-management',
  '/usage': 'ops-management',
  '/feature-flags': 'ops-management',
  '/company-roles': 'ops-management',
  '/legal-obligation-catalog': 'ops-management',
  '/legal-obligation-overlays': 'ops-management',
  '/chemical-links': 'ops-management',
  '/industries': 'classification',
  '/work-classifications': 'classification',
  '/industry-work-categories': 'classification',
  '/hazard-categories': 'classification',
  '/hazard-types': 'classification',
};

// 시스템 메뉴 (우상단 드롭다운)
const systemMenuItems: { path: string; label: string; icon: string; permissionKey: AdminPermissionKey | undefined }[] = [
  { path: '/profile', label: '내 정보', icon: '👤', permissionKey: undefined },
  // 관리자마다 다른 채널 — 서버 `settings/me`가 `monitoring-view` 뒤라 같은 키로 건다
  { path: '/ops-alert-settings', label: '운영 사건 알림 설정', icon: '🔔', permissionKey: 'monitoring-view' },
  { path: '/admins', label: '관리자 관리', icon: '🛡️', permissionKey: 'admin-management' },
  { path: '/roles', label: '관리자 역할 관리', icon: '🔑', permissionKey: 'admin-management' },
  { path: '/ai-settings', label: 'AI 설정', icon: '🤖', permissionKey: 'ai-settings' },
  { path: '/usage', label: '사용량 관리', icon: '📈', permissionKey: 'system-settings' },
  { path: '/risk-assessment', label: '위험성평가 설정', icon: '📊', permissionKey: 'system-settings' },
];

const SIDEBAR_COLLAPSED_KEY = 'admin-sidebar-collapsed';

// 경로별 쿼리 키 매핑
const pathQueryKeys: Record<string, string[][]> = {
  // 사이드바 메뉴
  '/': [['admin', 'dashboard']],
  '/organizations': [['admin', 'organizations']],
  '/companies': [['admin', 'companies']],
  '/sites': [['admin', 'sites']],
  '/users': [['admin', 'users']],
  '/inspections': [['admin', 'inspections']],
  '/reports': [['admin', 'reports']],
  '/tasks': [['admin', 'tasks']],
  '/feedback': [['admin', 'feedback']],
  '/user-feedback': [['admin', 'user-feedback']],
  '/measures': [['admin', 'measures'], ['admin', 'knowledge']],
  '/regulations': [['admin', 'regulations']],
  '/accident-cases': [['admin', 'accident-cases']],
  '/work-classifications': [['workClassifications'], ['workClassificationStats'], ['workClassificationCategories']],
  '/industry-work-categories': [['admin', 'industry-work-categories'], ['admin', 'industries', 'level1']],
  '/hazard-categories': [['admin', 'hazard-categories']],
  '/ai-matching': [['admin', 'ai-analyses']],
  '/ml-sources': [['admin', 'ml-photos'], ['admin', 'ml-improvement-images']],
  '/ai-usage': [['admin', 'ai-usage']],
  // 시스템 메뉴
  '/profile': [['admin', 'me']],
  '/admins': [['admin', 'admins']],
  '/roles': [['admin', 'roles']],
  '/ai-settings': [['admin', 'ai-providers'], ['admin', 'ai-prompts']],
  '/usage': [['admin', 'usage-config'], ['admin', 'usage-users']],
  '/release-notes': [['admin', 'release-notes']],
  // 소비 뷰 — 관리 뷰와 **다른 키**다. 두 화면이 다른 라우트를 치므로(서버 base도 다르다) 캐시도
  // 갈려 있어야 한다. 한 키를 공유하면 관리 쪽 변이(숨김·복구)가 소비 캐시를 **무효화하지 못하거나**
  // 그 반대가 되고, 그 어긋남은 **조용하다**(react-query가 지울 캐시를 못 찾고 통과한다).
  // ⚠️ 이 화면은 지금 react-query를 쓰지 않고 `useState`로 담는다 — 그래서 이 항목이 오늘 무효화할
  //    캐시는 **없다.** 그래도 두는 이유는 그 전환이 **라우트 표에 자리가 있을 때만 조용하지
  //    않기** 때문이다(없으면 전환한 날 아무도 그 누락을 말하지 않는다).
  '/release-note-feed': [['admin', 'release-note-feed']],
  // ⚠️ **손으로 베끼지 않는다** — 그 상수를 **import**한다(2026-10-21). 종전에는 같은 키를 여기
  //    리터럴로 적고 「두 자리가 어긋나면 무효화가 조용히 아무것도 안 한다 ⇒ 그 상수를 바꾸면 이
  //    줄을 함께 본다」는 **규칙**으로 막고 있었는데, 규칙은 잊으면 끝이고 그 실패는 red가 없다
  //    (키가 안 맞으면 react-query가 지울 캐시를 못 찾고 **조용히 통과**한다).
  // ⚠️ `pathQueryKeys`가 `Record<string, string[][]>`(mutable)이라 `as const` 배열을 직접 넣을 수
  //    없다 — 전개(`[...]`)해서 mutable 사본을 만든다. 그 한 겹이 유일한 마찰이다.
  // ⚠️ 이 아래 나머지 줄들은 정본 상수가 **없어서** 리터럴이다(그쪽은 이 처방을 적용할 수 없다).
  '/announcements': [[...ADMIN_ANNOUNCEMENT_QUERY_KEYS.list]],
  '/industries': [['admin', 'industries']],
  '/hazard-types': [['admin', 'hazard-types']],
  '/risk-assessment': [['admin', 'risk-assessment-configs']],
  '/feature-flags': [['admin', 'feature-flags']],
  '/company-roles': [['admin', 'company-rbac']],
  '/user-groups': [['admin', 'feature-flag-groups']],
  '/verification': [['admin', 'verification'], ['admin', 'designated-institutions']],
  '/message-send': [],
  '/external-services': [['admin', 'external-services']],
  '/sync-history': [['admin', 'sync-logs']],
  '/operations': [['operations', 'health']],
  // 정본 상수를 import한다(위 `/announcements`와 같은 처방) — 접두라 목록·요약·실행 기록이 함께 무효화된다
  '/ops-incidents': [[...OPS_INCIDENT_QUERY_KEYS.all]],
  '/ops-alert-settings': [[...OPS_INCIDENT_QUERY_KEYS.mySettings], [...OPS_INCIDENT_QUERY_KEYS.globalSettings]],
  // 필터 변종이 키 뒤에 객체로 붙으므로 **접두**로 적는다 — 그러면 변종 전부가 함께 무효화된다
  '/story-monitor': [['admin', 'stories']],
  // 정본 상수를 import한다(위 `/announcements`와 같은 처방) — 이력 키도 같은 접두라 함께 무효화된다
  '/legal-obligation-catalog': [[...LEGAL_OBLIGATION_CATALOG_QUERY_KEYS.list]],
  // 접두 — 기관 목록·기관별 변형·이력을 함께 무효화한다
  '/legal-obligation-overlays': [[...LEGAL_OBLIGATION_OVERLAY_QUERY_KEYS.all]],
  // 접두 — 목록·이력을 함께 무효화한다
  '/chemical-links': [[...CHEMICAL_LINKS_QUERY_KEYS.all]],
};

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();
  const { isEnabled } = useFeatureFlags();
  const [isSystemMenuOpen, setIsSystemMenuOpen] = useState(false);

  // 공지 팝업의 계정 축 — `super_admins.id`다(「봤음」이 `PopupView.superAdminId`로 가고 「오늘 하루」
  // 저장소 키도 이 값을 쓴다). null이면 공용 호스트가 아무것도 띄우지 않는다.
  const superAdminId = user?.id ?? null;

  const { data: permissionsData, isLoading: permLoading } = useQuery({
    queryKey: ['admin', 'my-permissions'],
    queryFn: () => adminApi.getMyPermissions(),
    staleTime: 5 * 60 * 1000, // 5분 캐시
  });

  const { data: pendingVerifData } = useQuery({
    queryKey: ['admin', 'verification', 'pending-count'],
    queryFn: () => getPendingVerifications({ page: 1, limit: 1 }),
    staleTime: 60 * 1000, // 1분 캐시
  });
  const pendingVerifCount = pendingVerifData?.meta?.total ?? 0;

  const { data: syncLogsStats } = useQuery({
    queryKey: ['admin', 'sync-logs', 'stats'],
    queryFn: () => syncLogsApi.getStats(),
    staleTime: 60 * 1000,
  });
  const syncFailureCount = syncLogsStats
    ? Object.values(syncLogsStats).reduce((total, sourceData) => {
        return total + Object.values(sourceData.subTypes).reduce((sum, st) => sum + (st.recentFailureCount ?? 0), 0);
      }, 0)
    : 0;
  const myPermissions = new Set(permissionsData?.data ?? []);
  // 권한 키가 없으면 항상 표시, 있으면 로딩 완료 후 해당 권한이 있을 때만 표시
  const hasPermission = (key: AdminPermissionKey | undefined) =>
    !key || (!permLoading && myPermissions.has(key));

  // 운영 사건 — 머리의 미확인 수. 이것이 「admin 화면」 채널이다(관리자별 설정 대상이 아니라 항상 보인다).
  // 감지기 낡음(`detector.stale`)도 함께 띄운다 — 감지기는 자기가 안 도는 것을 못 재므로 읽는 쪽이 본다.
  const canSeeOpsIncidents = hasPermission('monitoring-view');
  const { data: opsSummary } = useQuery({
    queryKey: OPS_INCIDENT_QUERY_KEYS.summary,
    queryFn: () => opsIncidentsApi.summary(),
    enabled: canSeeOpsIncidents,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
  const opsOpenCount = opsSummary?.openCount ?? 0;
  const opsDetectorStale = opsSummary?.detector.stale ?? false;
  const opsHasCritical = (opsSummary?.openBySeverity.CRITICAL ?? 0) > 0;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });

  // 그룹 펼침/접힘 상태 — 현재 경로가 속한 그룹은 자동 펼침
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    const groupId = GROUP_PATH_MAP[location.pathname];
    if (groupId) initial.add(groupId);
    return initial;
  });

  // 라우트 변경 시 해당 그룹 자동 펼침 (다른 그룹은 그대로 유지)
  useEffect(() => {
    const groupId = GROUP_PATH_MAP[location.pathname];
    if (groupId) {
      setExpandedGroups((prev) => {
        if (prev.has(groupId)) return prev;
        const next = new Set(prev);
        next.add(groupId);
        return next;
      });
    }
  }, [location.pathname]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const menuRef = useRef<HTMLDivElement>(null);

  // 사이드바 메뉴 스크롤 표시 (스크롤바를 숨겼으므로 상/하 스크롤 가능 화살표로 대체)
  const sidebarListRef = useRef<HTMLUListElement>(null);
  const [sidebarScroll, setSidebarScroll] = useState({ up: false, down: false });
  const updateSidebarScroll = useCallback(() => {
    const el = sidebarListRef.current;
    if (!el) return;
    const up = el.scrollTop > 1;
    const down = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setSidebarScroll((prev) => (prev.up === up && prev.down === down ? prev : { up, down }));
  }, []);

  // 마운트/리사이즈 시 스크롤 상태 갱신
  useEffect(() => {
    updateSidebarScroll();
    const el = sidebarListRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateSidebarScroll);
    ro.observe(el);
    window.addEventListener('resize', updateSidebarScroll);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSidebarScroll);
    };
  }, [updateSidebarScroll]);

  // 메뉴 콘텐츠 변경(권한 로드/그룹 펼침/사이드바 접힘) 시 스크롤 상태 갱신
  useEffect(() => {
    updateSidebarScroll();
  }, [expandedGroups, isSidebarCollapsed, permissionsData, pendingVerifCount, syncFailureCount, updateSidebarScroll]);

  // 메뉴 클릭 시 해당 경로의 쿼리 무효화 후 이동
  const handleMenuClick = (path: string) => {
    const queryKeys = pathQueryKeys[path];
    if (queryKeys) {
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
    navigate(path);
  };

  // 사이드바 상태 저장
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // 외부 클릭 시 메뉴 닫기
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsSystemMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSystemMenuItemClick = (path: string) => {
    setIsSystemMenuOpen(false);
    // 해당 경로의 쿼리 무효화 후 이동
    const queryKeys = pathQueryKeys[path];
    if (queryKeys) {
      queryKeys.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
    navigate(path);
  };

  // 현재 경로가 시스템 메뉴 항목인지 확인
  const isSystemMenuActive = systemMenuItems.some(
    (item) => item.path === location.pathname
  );

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-red-600 text-white shadow-lg relative z-40 flex-shrink-0">
        {/* 컴팩트 헤더 — py-4→py-2, 타이틀 text-xl→text-lg(세로 공간 확보, 2026-07-02). */}
        <div className="px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-bold">SafetyHub Admin</h1>
            <span className="px-2 py-0.5 bg-red-700 rounded text-sm">
              슈퍼관리자
            </span>
          </div>
          <div className="flex items-center gap-4">
            {canSeeOpsIncidents && opsSummary && (opsOpenCount > 0 || opsDetectorStale) && (
              <button
                type="button"
                onClick={() => handleMenuClick('/ops-incidents')}
                title={
                  opsDetectorStale
                    ? '안 돈 작업 감지기가 멈춘 것 같습니다 — 운영 사건 화면에서 확인하세요'
                    : `미확인 운영 사건 ${opsOpenCount}건`
                }
                className={`px-2 py-0.5 rounded text-sm font-medium ${
                  opsHasCritical || opsDetectorStale ? 'bg-yellow-300 text-red-900' : 'bg-red-800 text-white'
                } hover:opacity-90`}
              >
                운영 사건 {opsOpenCount}
                {opsDetectorStale ? ' · 감지기 멈춤' : ''}
              </button>
            )}
            <span className="text-sm">{user?.email}</span>

            {/* 시스템 메뉴 드롭다운 */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsSystemMenuOpen(!isSystemMenuOpen)}
                className={`p-1.5 rounded hover:bg-red-700 transition-colors ${
                  isSystemMenuActive || isSystemMenuOpen
                    ? 'bg-red-700'
                    : ''
                }`}
                title="시스템 메뉴"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>

              {/* 드롭다운 메뉴 */}
              {isSystemMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-card shadow-lg py-2 z-50">
                  {systemMenuItems.filter((item) => hasPermission(item.permissionKey)).map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleSystemMenuItemClick(item.path)}
                      className={`w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2 ${
                        location.pathname === item.path
                          ? 'bg-gray-100 font-medium'
                          : ''
                      }`}
                    >
                      <span>{item.icon}</span>
                      <span>{item.label}</span>
                    </button>
                  ))}
                  <hr className="my-2 border-gray-200" />
                  <button
                    onClick={() => {
                      setIsSystemMenuOpen(false);
                      logout();
                    }}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <span>🚪</span>
                    <span>로그아웃</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <nav
          className={`${
            isSidebarCollapsed ? 'w-16' : 'w-64'
          } bg-gray-900 text-gray-100 transition-all duration-300 ease-in-out flex flex-col overflow-hidden flex-shrink-0 relative z-40`}
        >
          {/* 토글 버튼 (상단) */}
          <div className="flex justify-end p-2">
            <button
              onClick={toggleSidebar}
              className="p-2 rounded hover:bg-gray-800 transition-colors"
              title={isSidebarCollapsed ? '메뉴 펼치기' : '메뉴 접기'}
            >
              <ChevronIcon
                className={`w-5 h-5 transition-transform duration-300 ${
                  isSidebarCollapsed ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>

          {/* 메뉴 아이템 (스크롤바 숨김 + 상/하 스크롤 가능 화살표 표시) */}
          <div className="relative flex-1 min-h-0">
            {/* 위로 스크롤 가능 표시 */}
            {sidebarScroll.up && (
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center bg-gradient-to-b from-gray-900 via-gray-900/80 to-transparent pb-2">
                <ChevronDownIcon className="w-4 h-4 rotate-180 text-gray-400" />
              </div>
            )}
            <ul
              ref={sidebarListRef}
              onScroll={updateSidebarScroll}
              className="h-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
            {sideNavItems.map((item, index) => {
              // 구분선 처리
              if ('type' in item && item.type === 'divider') {
                return (
                  <li key={`divider-${index}`} className="my-2 px-4">
                    <div className="border-t border-gray-700" />
                  </li>
                );
              }

              // 그룹 처리
              if ('type' in item && item.type === 'group') {
                // 권한 필터링: 보이는 자식이 하나도 없으면 그룹 자체 미노출
                const visibleChildren = item.items
                  .filter((child) => !child.flagKey || isEnabled(child.flagKey))
                  .filter((child) => hasPermission(child.permissionKey));

                if (visibleChildren.length === 0) return null;

                const isExpanded = expandedGroups.has(item.id);
                const isAnyChildActive = visibleChildren.some(
                  (child) =>
                    location.pathname === child.path ||
                    location.pathname.startsWith(child.path + '/'),
                );

                return (
                  <li key={`group-${item.id}`}>
                    <button
                      onClick={() => toggleGroup(item.id)}
                      title={isSidebarCollapsed ? item.label : undefined}
                      className={`w-full flex items-center gap-3 py-3 hover:bg-gray-800 transition-colors ${
                        isSidebarCollapsed ? 'px-4 justify-center' : 'px-6'
                      } ${
                        isAnyChildActive && !isExpanded
                          ? 'bg-gray-800 border-l-4 border-red-500'
                          : 'border-l-4 border-transparent'
                      }`}
                    >
                      {typeof item.icon === 'string' ? (
                        <span className="text-base flex-shrink-0">{item.icon}</span>
                      ) : (
                        <item.icon className="w-5 h-5 flex-shrink-0" />
                      )}
                      {!isSidebarCollapsed && (
                        <>
                          <span className="whitespace-nowrap flex-1 text-left">{item.label}</span>
                          <span className="text-xs text-gray-400">
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </>
                      )}
                    </button>
                    {/* 펼쳤을 때만 자식 렌더링 (collapsed 사이드바는 toggle 무력 — 자식 노출 X) */}
                    {!isSidebarCollapsed && isExpanded && (
                      <ul>
                        {visibleChildren.map((child) => {
                          const isActive =
                            location.pathname === child.path ||
                            location.pathname.startsWith(child.path + '/');
                          const ChildIcon = child.icon;
                          return (
                            <li key={child.path}>
                              <button
                                onClick={() => handleMenuClick(child.path)}
                                className={`w-full flex items-center gap-3 py-2 pl-12 pr-6 hover:bg-gray-800 transition-colors ${
                                  isActive
                                    ? 'bg-gray-800 border-l-4 border-red-500'
                                    : 'border-l-4 border-transparent'
                                }`}
                              >
                                {ChildIcon && <ChildIcon className="w-4 h-4 flex-shrink-0" />}
                                <span className="whitespace-nowrap flex-1 text-left text-sm">
                                  {child.label}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              // 일반 leaf 아이템 처리
              const leaf = item as NavLeaf;
              if (leaf.flagKey && !isEnabled(leaf.flagKey)) return null;
              if (!hasPermission(leaf.permissionKey)) return null;

              const isActive =
                location.pathname === leaf.path ||
                location.pathname.startsWith(leaf.path + '/');
              const IconComponent = leaf.icon;
              const badge =
                leaf.path === '/verification' && pendingVerifCount > 0
                  ? pendingVerifCount
                  : leaf.path === '/sync-history' && syncFailureCount > 0
                  ? syncFailureCount
                  : null;
              return (
                <li key={leaf.path}>
                  <button
                    onClick={() => handleMenuClick(leaf.path)}
                    title={isSidebarCollapsed ? leaf.label : undefined}
                    className={`w-full flex items-center gap-3 py-3 hover:bg-gray-800 transition-colors ${
                      isSidebarCollapsed ? 'px-4 justify-center' : 'px-6'
                    } ${
                      isActive
                        ? 'bg-gray-800 border-l-4 border-red-500'
                        : 'border-l-4 border-transparent'
                    }`}
                  >
                    {IconComponent && <IconComponent className="w-5 h-5 flex-shrink-0" />}
                    {!isSidebarCollapsed && (
                      <span className="whitespace-nowrap flex-1">{leaf.label}</span>
                    )}
                    {badge !== null && (
                      <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
            </ul>
            {/* 아래로 스크롤 가능 표시 */}
            {sidebarScroll.down && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent pt-2">
                <ChevronDownIcon className="w-4 h-4 text-gray-400" />
              </div>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main className="flex-1 min-w-0 overflow-y-auto p-8">
          <Outlet />
        </main>
      </div>

      {/*
        공지 팝업 호스트 — **2026-10-26 신설.** 그때까지 admin에는 팝업을 띄우는 코드가 **0건**이었고
        서버는 **이미 배선돼 있었다**: `popups-viewer.controller.ts`가 전역 `JwtAuthGuard`만 거쳐
        슈퍼관리자 토큰을 받고, `viewerIdentity()`가 `ADMIN` surface로 판정하며 「봤음」은
        `PopupView.superAdminId`에 들어간다. 즉 `ADMIN`을 겨냥해 게시된 공지가 **아무에게도 보이지
        않고 있었다** — 이 한 줄이 그 소비 표면이다.

        ⚠️ `<main>` **밖**에 둔다 — 포탈 모달이라 흐름에 없고, `<main>` 안에 두면 그 스크롤 컨테이너
        (`overflow-y-auto`)가 조상이 되어 오버레이가 컨텐츠 영역에 갇힌다(사이드바 위를 덮지 못한다).
        web 쪽 짝도 같은 이유로 레이아웃 트리의 형제다.

        ⚠️ `overlayZClassName`을 주입하지 않는다 — admin에는 web의 `MODAL_LAYER` 같은 층 상수 체계가
        **없고**(2026-10-26 census: 이 앱의 `overlayZClassName` 자리 4곳이 모두 `'z-[80]'` 리터럴),
        공용 `Modal`의 기본값이 `z-[70]`(= root 층)으로 이 자리에 맞다. 앱 열기 직후라 위에 다른
        모달이 없다. 리터럴을 박지 말 것 — 층 상수가 admin에 생기면 그때 그것을 주입한다.
      */}
      <PopupHost
        viewerId={superAdminId}
        fetchPopups={fetchActivePopups}
        markSeen={markPopupSeen}
        dismiss={dismissPopup}
        /*
         * ⛔ **`/release-notes`가 아니다.** admin에서 그 경로는 소비 화면이 아니라 「업데이트 내역
         * 관리」이고 `permissionKey: 'system-settings'`를 요구한다(위 `sideNavItems`의 그 두 항목) —
         * 서버가 저장하던 값이 **web 라우트**라 그대로 쓰면 **대부분의 운영자가 권한 게이트에
         * 막힌다**. admin의 소비 화면은 `/release-note-feed`(권한 키 없음)다. 세 갈래 계약은
         * `packages/ui`의 `resolvePopupLinks` JSDoc이 정본이다.
         */
        appLinkHref="/release-note-feed"
      />
    </div>
  );
}

// 아이콘 컴포넌트들
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
      />
    </svg>
  );
}

function OrganizationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function CompaniesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function SitesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function TasksIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function InspectionsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function ReportsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function AiMatchingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );
}

/** 머신러닝 소스 — 적층된 데이터 묶음(학습 소스) */
function MlSourcesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 7v10c0 1.657 3.582 3 8 3s8-1.343 8-3V7M4 7c0 1.657 3.582 3 8 3s8-1.343 8-3M4 7c0-1.657 3.582-3 8-3s8 1.343 8 3m0 5c0 1.657-3.582 3-8 3s-8-1.343-8-3"
      />
    </svg>
  );
}

function StoryMonitorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m-9 9h10a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function AIUsageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
      />
    </svg>
  );
}

function ReleaseNotesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

/** 전사 공지 — 확성기(heroicons `speakerphone`). 「우리가 말하는 자리」라 알림(종)과 구분된다. */
function AnnouncementsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
      />
    </svg>
  );
}

function FeedbackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
      />
    </svg>
  );
}

function MeasuresIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

function RegulationsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function AccidentCasesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function HazardTypesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function IndustriesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function WorkClassificationsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function IndustryWorkCategoriesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4M4 12h4M4 18h4M10 6h10M10 12h10M10 18h10" />
    </svg>
  );
}

function FeatureFlagsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
    </svg>
  );
}

function CompanyRolesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function UserGroupsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function MessageSendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  );
}

function ExternalServicesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function HazardCategoriesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

function UserFeedbackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
    </svg>
  );
}

function VerificationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function SyncHistoryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function OperationsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}

function ManualIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// 분류 체계 관리 그룹 — 폴더 선화(Composer 양식 탐색기 `ExplorerIcon`과 같은 형상)
function TaxonomyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 11h18" />
    </svg>
  );
}

function ReportComposerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}



